import { useCallback, useContext, useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { rem } from 'polished'
import styled from 'styled-components'
import { fetchProductSubscription } from 'actions/LuxPlusActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import GeoContext from 'contexts/geoContext'
import { buildUrl } from 'lib/url/searchUrlUtils'
import offerPageURL from 'lib/offer/offerPageURL'
import FormatCurrency from 'components/Common/FormatCurrency'
import Group from 'components/utils/Group'
import Pane from 'components/Common/Pane'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import IconButton from 'LuxKit/Button/IconButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TextLink from 'LuxKit/TextLink'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import ModalContext from 'contexts/ModalContext'
import { LUXURY_PLUS, LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import LuxPlusLogo from 'luxPlus/components/Logos/LuxPlusLogo'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import LuxPlusSubscriptionCancellationModal from 'luxPlus/components/LuxPlusSubscriptionCancellationModal'
import LuxPlusMonthTakeoverBenefitsList from './LuxPlusMonthTakeoverBenefitsList'
import { LUX_PLUS_MONTH_LANDING_PAGE_URL } from '../constants/luxPlusMonthTakeoverConstants'

const Header = styled(Pane)`
  position: relative;
`

const CloseButtonArea = styled.div`
  position: absolute;
  top: ${rem(12)};
  inset-inline-end: ${rem(12)};
`

interface Props {
  subscription: App.ProductSubscription
  canRedeemLuxPlusBenefits: boolean
  /** The accommodation offer this tag/modal was opened from, if any — for "Continue to offer" */
  offer?: App.AnyOffer
  /** Whether the tag was rendered on that offer's own page — swaps the CTA copy to "Return to offer" */
  onOfferPage?: boolean
}

function LuxPlusMonthTakeoverDetailsModal({ subscription, canRedeemLuxPlusBenefits, offer, onOfferPage = false }: Props) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { currentRegionCode } = useContext(GeoContext)
  const modalElement = useModalElementContext()
  const showModal = useContext(ModalContext)
  const subscriptionOffer = subscription.offers?.[0]

  useEffect(() => {
    dispatch(fetchProductSubscription())
  }, [dispatch])

  const handleClose = useCallback(() => {
    modalElement.resolve()
  }, [modalElement])

  const onShowCancellationPolicy = useCallback(() => {
    showModal(<LuxPlusSubscriptionCancellationModal offer={subscriptionOffer} />)
  }, [showModal, subscriptionOffer])

  const handleContinueToOffer = useCallback(() => {
    modalElement.resolve()
    if (offer && !onOfferPage) {
      navigate(buildUrl(offerPageURL(offer), currentRegionCode))
    }
  }, [modalElement, offer, onOfferPage, navigate, currentRegionCode])

  return <ModalBase dismissible onClose={handleClose}>
    <ModalBody>
      <Header background="lux-plus" paletteType="inverse" padding={20}>
        <CloseButtonArea>
          <IconButton
            kind="tertiary"
            variant="dark"
            onClick={handleClose}
            aria-label={intl.formatMessage({
              id: 'afr2lC45iPIVeB4P-Fvz',
              defaultMessage: 'close',
              description: 'Aria label for close button in LuxPlus Month details modal',
            })}
          >
            <LineTimesIcon />
          </IconButton>
        </CloseButtonArea>
        <Group direction="vertical" gap={12}>
          <BodyText variant="medium" colour="lux-plus-contrast">
            <FormattedMessage
              id="Ji8vprfCwBFlySy9uQXt"
              defaultMessage="Exclusive {logo} Member Event"
              description="Small label at the top of the LuxPlus Month details modal header"
              values={{ logo: <LuxPlusLogo colour="inherit" height={10} /> }}
            />
          </BodyText>
          <Heading variant="heading2" colour="lux-plus-contrast">
            <FormattedMessage
              id="Oskf6ALK80hBIqfJXJRW"
              defaultMessage="<i>Bigger</i> savings.<br></br>Same unforgettable escapes."
              description="Headline in the LuxPlus Month details modal header"
              values={{
                i: (chunks) => <i>{chunks}</i>,
                br: () => <br />,
              }}
            />
          </Heading>
        </Group>
      </Header>
      <ModalContent>
        <Group direction="vertical" gap={16}>
          {subscription.fetching && <LoadingIndicator />}
          {canRedeemLuxPlusBenefits && <BodyText variant="medium">
            <FormattedMessage
              id="d4B8XFCAWR8qvo5dRcQ7"
              defaultMessage="For a limited time, enjoy even more value from your membership with elevated member benefits and extra savings on select offers across hotels, flights, tours and experiences."
              description="Intro copy in the LuxPlus Month details modal for existing members"
            />
          </BodyText>}
          {!canRedeemLuxPlusBenefits && subscriptionOffer && <>
            <BodyText variant="medium">
              <FormattedMessage
                id="nUEEye_JO2Y1bGo2cild"
                defaultMessage="Join {programName} to unlock this exclusive offer at an exceptional member price – plus 12 months of member-only benefits. Your {price} annual membership fee will be added to your total at checkout."
                description="Intro copy in the LuxPlus Month details modal explaining the membership fee"
                values={{
                  programName: LUXURY_PLUS.PROGRAM_NAME,
                  price: <FormatCurrency value={subscriptionOffer.price} format="roundedDollar" />,
                }}
              />
            </BodyText>
            <Group direction="vertical" gap={8}>
              <LuxPlusMonthTakeoverBenefitsList
                showSavingsBenefit
                joinFee={subscriptionOffer.joinFee}
                showAdditionalBenefits
              />
            </Group>
            <BodyText variant="small" colour="normal-contrast">
              <FormattedMessage
                id="CanE-G5IRhJszGjKQWEw"
                defaultMessage="{programName} {price} annual membership renews on sign-up anniversary date. <cancelLink>Cancel renewal anytime.</cancelLink> <termsLink>T&Cs apply.</termsLink>"
                description="Fine print in the LuxPlus Month details modal describing membership renewal terms"
                values={{
                  programName: LUXURY_PLUS.PROGRAM_NAME,
                  price: <FormatCurrency value={subscriptionOffer.price} format="roundedDollar" />,
                  cancelLink: (chunks) => <TextLink underline={false} weight="bold" colour="highlight-primary-dark" size="small" endIcon={<LineInfoCircleIcon colour="normal-contrast" size="XS" />} onClick={onShowCancellationPolicy}>{chunks}</TextLink>,
                  termsLink: (chunks) => <TextLink weight="regular" size="small" to={`/${LUXURY_PLUS_PATHS.TERMS_AND_CONDITIONS_PAGE}`} onClick={handleClose}>{chunks}</TextLink>,
                }}
              />
            </BodyText>
          </>}
        </Group>
      </ModalContent>
    </ModalBody>
    <ModalFooter
      actionsLayout="stacked"
      tabletActionsLayout="auto"
      primaryActionProps={{
        children: onOfferPage
          ? intl.formatMessage({
              id: 'BGrCbU4O3Bcqp2HyRGhB',
              defaultMessage: 'Return to offer',
              description: 'Primary action button in the LuxPlus Month details modal when opened from the offer\'s own page',
            })
          : intl.formatMessage({
              id: 'Ua0FbQRsVUwmY1463YwW',
              defaultMessage: 'Continue to offer',
              description: 'Primary action button in the LuxPlus Month details modal',
            }),
        onClick: handleContinueToOffer,
      }}
      secondaryActionProps={{
        children: intl.formatMessage({
          id: 'HLAU14j68VYupPAivcF7',
          defaultMessage: 'View all {programName} offers',
          description: 'Secondary action link in the LuxPlus Month details modal',
        }, { programName: LUXURY_PLUS.PROGRAM_NAME }),
        to: LUX_PLUS_MONTH_LANDING_PAGE_URL,
      }}
    />
  </ModalBase>
}

const mapStateToProps = (state: App.State) => ({
  subscription: state.luxPlus.products.subscription,
  canRedeemLuxPlusBenefits: getCanRedeemLuxPlusBenefits(state),
})

export default connect(mapStateToProps)(LuxPlusMonthTakeoverDetailsModal)
